import { lazy, Suspense, useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import posthog from 'posthog-js'
import { Login } from '@/pages/Auth/Login'
import { ProtectedRoute } from '@/shared/components/ProtectedRoute'
import { AppLayout } from '@/shared/components/AppLayout'

// Route-level code splitting — every page except Login/ProtectedRoute/AppLayout
// loads on demand. Biggest wins: Order, Restock, Adjustment.
const Landing = lazy(() => import('@/pages/Landing/Landing').then(m => ({ default: m.Landing })))
const Signup = lazy(() => import('@/pages/Auth/Signup').then(m => ({ default: m.Signup })))
const ForgotPassword = lazy(() => import('@/pages/Auth/ForgotPassword').then(m => ({ default: m.ForgotPassword })))
const ResetPassword = lazy(() => import('@/pages/Auth/ResetPassword').then(m => ({ default: m.ResetPassword })))
const NotFound = lazy(() => import('@/pages/NotFound').then(m => ({ default: m.NotFound })))
const Onboarding = lazy(() => import('@/pages/Onboarding/Onboarding').then(m => ({ default: m.Onboarding })))
const Restore = lazy(() => import('@/pages/Settings/Restore').then(m => ({ default: m.Restore })))
const Dashboard = lazy(() => import('@/pages/Dashboard/Dashboard').then(m => ({ default: m.Dashboard })))
const Products = lazy(() => import('@/pages/Products/Products').then(m => ({ default: m.Products })))
const Order = lazy(() => import('@/pages/Order/Order').then(m => ({ default: m.Order })))
const Restock = lazy(() => import('@/pages/Restock/Restock').then(m => ({ default: m.Restock })))
const History = lazy(() => import('@/pages/History/History').then(m => ({ default: m.History })))
const Settings = lazy(() => import('@/pages/Settings/Settings').then(m => ({ default: m.Settings })))
const Credit = lazy(() => import('@/pages/Credit/Credit').then(m => ({ default: m.Credit })))
const Adjustment = lazy(() => import('@/pages/Adjustment/Adjustment').then(m => ({ default: m.Adjustment })))
const Statement = lazy(() => import('@/pages/Statement/Statement').then(m => ({ default: m.Statement })))
const PrivacyPolicy = lazy(() => import('@/pages/Legal/PrivacyPolicy').then(m => ({ default: m.PrivacyPolicy })))
const TermsOfService = lazy(() => import('@/pages/Legal/TermsOfService').then(m => ({ default: m.TermsOfService })))

function PostHogPageviewTracker() {
  const location = useLocation()
  useEffect(() => {
    posthog.capture('$pageview', { $current_url: window.location.href })
  }, [location])
  return null
}

function RouteFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="size-5 rounded-full border-2 border-primary border-t-transparent animate-spin" />
    </div>
  )
}

export function AppRouter() {
  return (
    <BrowserRouter>
      <PostHogPageviewTracker />
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/stock" element={<Navigate to="/dashboard" replace />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route
            path="/onboarding"
            element={<ProtectedRoute requireOrg={false}><Onboarding /></ProtectedRoute>}
          />
          <Route
            path="/restore"
            element={<ProtectedRoute requireOrg={false}><Restore /></ProtectedRoute>}
          />
          <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/products" element={<Products />} />
            <Route path="/session/order" element={<Order />} />
            <Route path="/session/restock" element={<Restock />} />
            <Route path="/history" element={<History />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/credit" element={<Credit />} />
            <Route path="/session/adjustment" element={<Adjustment />} />
          </Route>
          {/* Public customer-facing statement link — no auth */}
          <Route path="/s/:token" element={<Statement />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  )
}
